import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Command, Circle, Clock, Trophy } from 'lucide-react';
import { useSearchExplorer } from '../../contexts/SearchExplorerContext';
import proService, { ProEvent } from '../../services/proService';

const ACCENT = '#3fced1';
const ROTATING_WORDS = ['Turniere', 'Vereine', 'Spielstätten', 'Dart-Shops'];

/**
 * Hero-Sektion der Startseite – Port des Flutter SearchHeader.
 * Branding + animierte Headline + Such-Trigger (öffnet SearchExplorer),
 * daneben eine "Spiele heute"-Karte (Pro Events).
 */
const HeroSection: React.FC = () => {
  const { open: openSearch } = useSearchExplorer();
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
    }, 2200);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative overflow-hidden border-b border-zinc-900">
      {/* Glow-Hintergrund */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 80% at 20% 10%, rgba(63,206,209,0.15), transparent 60%), radial-gradient(50% 60% at 90% 20%, rgba(63,206,209,0.08), transparent 60%)',
        }}
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.2fr_0.8fr] lg:py-20">
        {/* Linke Spalte: Branding + Suche */}
        <div className="flex flex-col justify-center">
          <div className="mb-6 flex items-center gap-3">
            <img
              src="/icon.png"
              alt="DartCloud"
              className="h-11 w-11 rounded-xl object-cover"
            />
            <span className="text-xl font-bold text-white">DartCloud</span>
          </div>

          <h1 className="text-4xl font-extrabold leading-tight text-white sm:text-5xl">
            Finde jetzt
            <br />
            <span style={{ color: ACCENT }}>{ROTATING_WORDS[wordIndex]}</span>
            <br />
            in deiner Nähe
          </h1>

          <p className="mt-5 max-w-md text-base text-gray-400">
            DartCloud bietet dir eine präzise Suche nach allem rund um den Dartsport.
          </p>

          {/* Such-Trigger */}
          <button
            type="button"
            onClick={openSearch}
            className="mt-7 flex h-14 w-full max-w-md items-center gap-3 rounded-2xl border border-zinc-700 bg-zinc-800/70 px-4 text-left transition-colors hover:border-zinc-600 hover:bg-zinc-800"
          >
            <Search size={22} style={{ color: ACCENT }} />
            <span className="flex-1 text-[15px] text-gray-500">Suchen...</span>
            <span
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white"
              style={{ backgroundColor: ACCENT }}
            >
              <Command size={13} /> K
            </span>
          </button>
        </div>

        {/* Rechte Spalte: Spiele heute */}
        <SpieleHeuteCard />
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Spiele heute – Pro Events des heutigen Tages
// ---------------------------------------------------------------------------

const SpieleHeuteCard: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ProEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    proService
      .getEventsByDateWithStats(0)
      .then((list) => {
        if (!cancelled) setEvents(Array.isArray(list) ? list.slice(0, 4) : []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/60">
      <div className="px-5 py-4">
        <h2 className="text-lg font-bold text-white">Spiele heute</h2>
      </div>
      <div className="h-px bg-zinc-800" />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div
            className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-700"
            style={{ borderTopColor: ACCENT }}
          />
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <Trophy size={40} className="text-zinc-700" />
          <span className="text-sm text-gray-500">Keine Spiele heute</span>
        </div>
      ) : (
        <div>
          {events.map((event, idx) => (
            <SpielItem key={event.id || event._id || idx} event={event} />
          ))}
          <button
            type="button"
            onClick={() => navigate('/spiele')}
            className="w-full px-5 py-3.5 text-sm font-semibold transition-colors hover:bg-zinc-800/60"
            style={{ color: ACCENT }}
          >
            Alle anzeigen
          </button>
        </div>
      )}
    </div>
  );
};

const SpielItem: React.FC<{ event: ProEvent }> = ({ event }) => {
  const isLive = proService.isLive(event);
  const scheduled = proService.isScheduled(event);
  const scores = proService.formatScore(event);
  const homeWon = event.winner === 1;
  const awayWon = event.winner === 2;
  const homeName = proService.getParticipantName(event.homeParticipant);
  const awayName = proService.getParticipantName(event.awayParticipant);

  const nameClass = (won: boolean) =>
    `flex-1 truncate text-sm ${won ? 'font-bold' : 'font-medium text-white'}`;
  const nameStyle = (won: boolean) => (won ? { color: ACCENT } : undefined);

  return (
    <div className="border-b border-zinc-800 px-5 py-3.5 last:border-b-0">
      {event.tournament?.name && (
        <p className="mb-2 truncate text-xs font-medium text-gray-500">
          {event.tournament.name}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <span className={nameClass(homeWon)} style={nameStyle(homeWon)}>
          {homeName}
        </span>
        {!scheduled && (
          <span
            className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums"
            style={
              homeWon
                ? { backgroundColor: 'rgba(63,206,209,0.1)', color: ACCENT }
                : { backgroundColor: '#27272a', color: '#a1a1aa' }
            }
          >
            {scores.home}
          </span>
        )}
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className={nameClass(awayWon)} style={nameStyle(awayWon)}>
          {awayName}
        </span>
        {!scheduled && (
          <span
            className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums"
            style={
              awayWon
                ? { backgroundColor: 'rgba(63,206,209,0.1)', color: ACCENT }
                : { backgroundColor: '#27272a', color: '#a1a1aa' }
            }
          >
            {scores.away}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        {isLive ? (
          <>
            <Circle size={10} className="animate-pulse fill-red-500 text-red-500" />
            <span className="text-xs font-semibold text-red-500">Live</span>
          </>
        ) : (
          <>
            <Clock size={12} className="text-gray-500" />
            <span className="text-xs text-gray-500">
              {proService.formatEventTime(event.startTime)}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default HeroSection;
